import { logger } from 'src/commons/utils/logger';

export const LEGACY_METRICS_KEY = 'enableLegacyConversionMetrics';

const ENTITY_ID_PREFIX = 'ENTITY';

/**
 * Resolves the entityId for campaign-manager paths using a fallback chain:
 * 1. The clicked link's ?entityId param (if it starts with "ENTITY")
 * 2. The entityId parsed from the current page URL's path (e.g. /dsp/ENTITY123/...)
 * 3. The current page URL's ?entityId param (if it starts with "ENTITY")
 */
function resolveEntityId(linkUrl: URL, currentPageHref: string): string | null {
  const linkEntityId = linkUrl.searchParams.get('entityId');
  if (linkEntityId?.startsWith(ENTITY_ID_PREFIX)) return linkEntityId;

  let currentUrl: URL;
  try {
    currentUrl = new URL(currentPageHref);
  } catch (e) {
    logger.warn(
      'urlRewriteRules: failed to parse current page URL',
      e instanceof Error ? e : new Error(String(e)),
    );
    return null;
  }

  const pathEntityId = extractEntityIdFromPath(currentUrl.pathname);
  if (pathEntityId) return pathEntityId;

  const currentParamEntityId = currentUrl.searchParams.get('entityId');
  if (currentParamEntityId?.startsWith(ENTITY_ID_PREFIX))
    return currentParamEntityId;

  return null;
}

function extractEntityIdFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/dsp\/([^/]+)/);
  if (match && match[1].startsWith(ENTITY_ID_PREFIX)) return match[1];
  return null;
}

interface RewriteRule {
  pattern: RegExp;
  rewrite: (url: URL, currentPageHref: string) => URL | null;
}

const REWRITE_RULES: RewriteRule[] = [
  // Rule 1: /campaign-manager/all-campaigns -> entity/orders
  {
    pattern: /^\/campaign-manager\/all-campaigns(\/|$)/,
    rewrite: (url: URL, currentPageHref: string): URL | null => {
      // If query params has switchFromEntity then we are switching to a different
      // entity from the global account selector. We do not rewrite the URL as this
      // could be a sponsored account.
      if (url.searchParams.has('switchFromEntity')) return null;

      const entityId = resolveEntityId(url, currentPageHref);
      if (!entityId) return null;

      const rewritten = new URL(
        `/dsp/${encodeURIComponent(entityId)}/orders`,
        url.origin,
      );
      rewritten.searchParams.set(LEGACY_METRICS_KEY, 'true');
      return rewritten;
    },
  },
  // Rule 2: /campaign-manager/all-ad-groups → line-items
  {
    pattern: /^\/campaign-manager\/all-ad-groups(\/|$)/,
    rewrite: (url: URL, currentPageHref: string): URL | null => {
      const entityId = resolveEntityId(url, currentPageHref);
      if (!entityId) return null;

      const rewritten = new URL(
        `/dsp/${encodeURIComponent(entityId)}/line-items`,
        url.origin,
      );
      rewritten.searchParams.set(LEGACY_METRICS_KEY, 'true');
      return rewritten;
    },
  },
  // Rule 3: /dsp/{entityId}/overview → entity/orders
  {
    pattern: /^\/dsp\/[^/]+\/overview(\/|$)/,
    rewrite: (url: URL): URL | null => {
      const entityId = extractEntityIdFromPath(url.pathname);
      if (!entityId) return null;

      const rewritten = new URL(
        `/dsp/${encodeURIComponent(entityId)}/orders`,
        url.origin,
      );
      rewritten.searchParams.set(LEGACY_METRICS_KEY, 'true');
      return rewritten;
    },
  },
  // Rule 4: /dsp/{entityId}/orders or /dsp/{entityId}/line-items — append param
  {
    pattern: /^\/dsp\/[^/]+\/(orders|line-items)\/?$/,
    rewrite: (url: URL): URL | null => {
      if (url.searchParams.get(LEGACY_METRICS_KEY) === 'true') return null;

      const rewritten = new URL(url.href);
      rewritten.searchParams.set(LEGACY_METRICS_KEY, 'true');
      return rewritten;
    },
  },
  // Rule 5: /dsp|aap/{entityId}/advertisers/{advertiserId}/orders or line-items — append param
  {
    pattern: /^\/(dsp|aap)\/[^/]+\/advertisers\/[^/]+\/(orders|line-items)\/?$/,
    rewrite: (url: URL): URL | null => {
      if (url.searchParams.get(LEGACY_METRICS_KEY) === 'true') return null;

      const rewritten = new URL(url.href);
      rewritten.searchParams.set(LEGACY_METRICS_KEY, 'true');
      return rewritten;
    },
  },
];

export function getRewrittenUrl(
  href: string,
  currentPageHref: string,
): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch (e) {
    logger.warn(
      'urlRewriteRules: failed to parse href',
      e instanceof Error ? e : new Error(String(e)),
    );
    return null;
  }

  for (const rule of REWRITE_RULES) {
    if (rule.pattern.test(url.pathname)) {
      const rewritten = rule.rewrite(url, currentPageHref);
      if (rewritten) return rewritten.href;
      return null;
    }
  }

  return null;
}

/**
 * Appends enableLegacyConversionMetrics=true to a URL if the feature is
 * active in session storage and the param is not already present.
 * Mutates the URL in place.
 */
export function appendLegacyMetricsParamIfEnabled(url: URL): void {
  try {
    if (window.sessionStorage.getItem(LEGACY_METRICS_KEY) !== 'true') return;
    if (url.searchParams.get(LEGACY_METRICS_KEY) === 'true') return;
    url.searchParams.set(LEGACY_METRICS_KEY, 'true');
    logger.info(
      `urlRewriteRules: appending legacy metrics param for region switch to ${url.host}`,
    );
  } catch (e) {
    logger.warn(
      'urlRewriteRules: failed to append legacy metrics param',
      e instanceof Error ? e : new Error(String(e)),
    );
  }
}
